/**
 * Bausteine des prozeduralen Mysteriums.
 *
 * Die zentrale Idee: Das Erzaehlskelett ist fest und handgeschrieben, der
 * Inhalt wird ausgetauscht. Das Skelett besteht aus typisierten Hinweis-Slots,
 * und jede Aufloesung liefert Inhalt fuer *jeden* Slot-Typ. Damit ist das
 * Pacing garantiert, waehrend die Wahrheit pro Durchlauf wechselt.
 */

export type MysteryId =
  | 'visitors'
  | 'leviathan'
  | 'meridian'
  | 'descendants'
  | 'rift'
  | 'cartographers'
  | 'wreckers'
  | 'echo';

/*
 * Acht Aufloesungen statt fuenf.
 *
 * Gezogen werden pro Durchlauf immer nur drei — eine wahre und zwei falsche
 * Faehrten —, an der Verteilung der weichen Beats aendert sich also nichts.
 * Was sich aendert, ist die Zahl der moeglichen Kombinationen: aus 60 werden
 * 336. Ein zweiter Durchlauf trifft damit deutlich seltener auf dieselbe Lage.
 *
 * Die drei neuen besetzen bewusst andere Erklaerungsraeume: eine
 * gleichgueltig-behoerdliche, eine vollstaendig menschliche und eine, in der
 * gar niemand handelt.
 */
export const MYSTERY_IDS: readonly MysteryId[] = [
  'visitors',
  'leviathan',
  'meridian',
  'descendants',
  'rift',
  'cartographers',
  'wreckers',
  'echo',
];

/**
 * Weiche Slots: Was Menschen berichten und was Geraete anzeigen.
 * Beides ist ehrlich deutbar — genau deshalb duerfen falsche Faehrten hier
 * stehen, ohne dass der Spieler je angelogen wird.
 */
export const SOFT_SLOTS = ['witness_testimony', 'instrument_reading', 'anomaly_signature'] as const;

/**
 * Harte Slots: Was man in die Hand nehmen kann, was mit den Vermissten geschah,
 * und wer sonst noch vor Ort war. Diese gehoeren ausschliesslich der Wahrheit.
 */
export const HARD_SLOTS = [
  'physical_artifact',
  'missing_person_trace',
  'third_party_interference',
] as const;

export type SoftSlot = (typeof SOFT_SLOTS)[number];
export type HardSlot = (typeof HARD_SLOTS)[number];
export type ClueSlot = SoftSlot | HardSlot;

export const ALL_SLOTS: readonly ClueSlot[] = [...SOFT_SLOTS, ...HARD_SLOTS];

export function isSoftSlot(slot: ClueSlot): slot is SoftSlot {
  return (SOFT_SLOTS as readonly string[]).includes(slot);
}

/**
 * Wie viele Textvarianten jeder Slot je Aufloesung braucht.
 *
 * Die Zahlen sind nicht beliebig: Sie ergeben sich daraus, wie oft ein Slot im
 * Skelett vorkommt und wie viele davon im ungünstigsten Fall auf dieselbe
 * Aufloesung fallen koennen. Zu wenige Varianten hiessen, dass sich Hinweise
 * innerhalb eines Durchlaufs wiederholen.
 */
export const VARIANTS_PER_SLOT: Readonly<Record<ClueSlot, number>> = {
  witness_testimony: 3,
  instrument_reading: 3,
  anomaly_signature: 3,
  physical_artifact: 2,
  missing_person_trace: 2,
  third_party_interference: 2,
};

/** Gewicht eines Hinweises in der Beweiswuerdigung. */
export const SLOT_WEIGHT: Readonly<Record<ClueSlot, number>> = {
  witness_testimony: 1,
  instrument_reading: 1,
  anomaly_signature: 1,
  physical_artifact: 3,
  missing_person_trace: 3,
  third_party_interference: 3,
};

export interface ClueTemplate {
  readonly id: string;
  readonly mystery: MysteryId;
  readonly slot: ClueSlot;
  /** Uebersetzungsschluessel des Fundtextes. */
  readonly textKey: string;
  readonly weight: number;
}

export interface MysteryDefinition {
  readonly id: MysteryId;
  readonly nameKey: string;
  /** Der Satz, mit dem der Fall im Finale aufgeloest wird. */
  readonly revelationKey: string;
  readonly clues: readonly ClueTemplate[];
}
